/**
 * Generic dialog for invoking a workflow action.
 *
 * Renders the action's `capture_fields` (text/number/select/textarea/
 * boolean/date) plus an optional reason field through the shared
 * generated-form pipeline (`useForm`), then calls
 * `useExecuteWorkflowAction`. Handles four result paths:
 *   - immediate success (closes + emits onSuccess)
 *   - validation error (renders inline)
 *   - PendingTransition created (renders an "in review" banner)
 *   - BlockedByIssue (renders inline links to the open WorkflowIssues)
 *
 * Preconditions
 * -------------
 *
 * Actions can declare a polymorphic ``preconditions`` list (see
 * :class:`PreconditionSpec`). Blocking preconditions render above the
 * capture fields and must be satisfied before the form submits - the
 * collected results are posted back inside
 * ``captured_values.preconditions`` so the engine can validate them.
 * Non-blocking preconditions render in the same area as informational
 * panels (read-only briefings, advisory checklists).
 */

import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { extractApiErrorMessage } from '@/shared/lib/api-error'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import {
  useCreateExceptionGrant,
  useExceptionReasons,
  useExecuteWorkflowAction,
} from '@/shared/hooks/use-workflows'
import type {
  CaptureFieldSpec,
  PreconditionSpec,
  WorkflowAction,
  WorkflowIssue,
  WorkflowIssueResolvePayload,
} from '@/shared/lib/workflows/types'
import { ExceptionModal } from '@/admin/components/workflow-issues/workflow-issue-resolve-modal'

import { PreconditionsPanel } from './preconditions-panel'

import { useWorkflowActionLabel } from './use-workflow-i18n'

export interface WorkflowActionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  instanceUuid: string
  action: WorkflowAction
  /** Optional workflow key to scope the action-label translation. */
  workflowKey?: string
  onSuccess?: () => void
}

type FormValues = Record<string, unknown>

/** Resolve a capture field's ``default`` into an initial input value. */
function defaultValueFor(field: CaptureFieldSpec): string {
  if (!field.default) return ''
  if (field.type === 'date' && field.default === 'today') {
    // Local date parts, not toISOString() (UTC) - otherwise the prefilled
    // "today" can shift a day for users behind/ahead of UTC.
    const d = new Date()
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
    ].join('-')
  }
  return field.default
}

function toFormField(field: CaptureFieldSpec): FormField {
  const common = {
    name: field.key,
    label: field.label || field.key,
    placeholder: field.placeholder,
    hint: field.help_text,
    required: field.required ? `${field.label || field.key} is required.` : undefined,
  }

  switch (field.type) {
    case 'boolean':
      return { ...common, type: 'checkbox', value: false }
    case 'number':
      return {
        ...common,
        type: 'number',
        value: '',
        validationSchema: field.required
          ? z.number().min(1, `${field.label || field.key} is required.`)
          : z.number().nullable(),
      }
    case 'select':
      return {
        ...common,
        type: 'select',
        // The server only sends a default the select offers.
        value: field.default ?? '',
        options: (field.options || []).map((o) => ({
          label: o.label,
          value: o.value,
        })),
      }
    case 'textarea':
      return { ...common, type: 'textarea', value: '' }
    case 'date':
      return {
        ...common,
        type: 'datepicker',
        value: defaultValueFor(field),
        disablePast: field.disable_past,
        disableFuture: field.disable_future,
      }
    case 'datetime':
      return {
        ...common,
        type: 'datetime',
        value: field.default && field.default !== 'today' ? field.default : '',
        disablePast: field.disable_past,
        disableFuture: field.disable_future,
      }
    case 'entity-search':
      return {
        ...common,
        type: 'entity-search',
        value: null,
        configurationData: {
          lookup_provider: field.lookup_provider,
          provider_params: field.provider_params,
          allow_create: field.allow_create,
        },
      }
    case 'file':
    case 'picture':
      // Renders the shared FileUploader (generated-input's FileField). Single
      // vs multiple is driven purely by ``configuration_data.file_multiple``
      // (forwarded as ``field.multiple``); ``file_use_case`` selects the upload
      // endpoint and accepted MIME types. A multi-file value is an array of
      // upload envelopes, which the backend coerces to a list of UUIDs.
      return {
        ...common,
        type: field.type === 'picture' ? 'picture' : 'file',
        value: field.multiple ? [] : null,
        multiple: field.multiple,
        useCase: field.file_use_case,
      }
    case 'text':
    default:
      return { ...common, type: 'text', value: defaultValueFor(field) }
  }
}

export function WorkflowActionDialog({
  open,
  onOpenChange,
  instanceUuid,
  action,
  workflowKey,
  onSuccess,
}: WorkflowActionDialogProps) {
  const { t } = useTranslation()
  const captureFields = action.capture_fields
  const actionLabel = useWorkflowActionLabel(
    action.key,
    action.label,
    workflowKey,
  )
  // governance_config.requires_reason is the source of truth (mirrored
  // onto the action serializer). We still tolerate the legacy
  // ui_hints.require_reason flag so old fixtures keep working.
  //
  // When the action is exception-gated, the exception modal is the single
  // confirmation + reason surface (WPS-117): we suppress this second required
  // reason field so the user is never asked to justify the same action twice.
  const requireReason =
    (Boolean(action.requires_reason) ||
      Boolean(action.ui_hints?.['require_reason'])) &&
    !action.exception_binding
  const reasonMinLength = Math.max(0, action.reason_min_length ?? 0)

  const [pendingMessage, setPendingMessage] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [blockingIssues, setBlockingIssues] = useState<WorkflowIssue[]>([])
  const [exceptionModalOpen, setExceptionModalOpen] = useState(false)
  const [exceptionGrantId, setExceptionGrantId] = useState<string | null>(null)
  // Captured form values stashed when submit defers to the exception modal, so
  // granting the exception completes the action without a second submit
  // (WPS-117 - the exception modal IS the confirmation).
  const [pendingCaptured, setPendingCaptured] = useState<Record<
    string,
    unknown
  > | null>(null)
  const preconditions: PreconditionSpec[] = action.preconditions ?? []
  // Tracks user-facing precondition state (checkbox values, checklist
  // toggles, etc.). The renderer owns the shape per ``kind`` - we
  // just thread the bag through and submit it whole on confirm.
  const [preconditionResults, setPreconditionResults] = useState<
    Record<string, unknown>
  >({})

  const mutation = useExecuteWorkflowAction(instanceUuid)
  const createExceptionGrant = useCreateExceptionGrant()
  const { data: exceptionReasons = [] } = useExceptionReasons()
  const exceptionBinding = action.exception_binding

  function isPreconditionSatisfied(spec: PreconditionSpec): boolean {
    if (!spec.blocking) return true
    const value = preconditionResults[spec.key]
    switch (spec.kind) {
      case 'acknowledgement':
        return value === true
      case 'checklist': {
        const items =
          (spec.display_payload?.items as
            | Array<{ key: string }>
            | undefined) ?? []
        const ticks = (value as Record<string, boolean> | undefined) ?? {}
        return items.every((item) => ticks[item.key] === true)
      }
      case 'briefing':
        // Read-only document. We treat it as satisfied if the user
        // simply opened the dialog (display_payload.acknowledge ===
        // true requires an explicit tick - wire it as an
        // ``acknowledgement`` precondition instead).
        return true
      case 'attachment': {
        const files = (value as unknown[] | undefined) ?? []
        return files.length > 0
      }
      default:
        return true
    }
  }

  const allPreconditionsMet = preconditions.every(isPreconditionSatisfied)

  // Single execution path shared by the direct submit and the
  // exception-modal-completes-the-action flow (WPS-117).
  const runAction = useCallback(
    async (
      captured: Record<string, unknown>,
      reasonText: string,
      grantId: string | null,
    ) => {
      setFormError(null)
      setBlockingIssues([])
      try {
        const result = await mutation.mutateAsync({
          action_key: action.key,
          captured_values: captured,
          reason_text: reasonText || undefined,
          exception_grant_id: grantId || undefined,
        })
        if (!result.ok && (result.issues?.length ?? 0) > 0) {
          setBlockingIssues(result.issues)
          return
        }
        if (result.pending_transition) {
          setPendingMessage(
            "Submitted to a second approver. You'll see the request in Pending Approvals.",
          )
          onSuccess?.()
          return
        }
        toast.success(`${actionLabel} executed.`)
        onSuccess?.()
        onOpenChange(false)
      } catch (err) {
        // ``BlockedByIssue`` from the API is surfaced via the ``issues`` array
        // in the error body (``result.issues``), or on ``cause`` where a
        // client bubbles the JSON onto the thrown Error.
        const failed = err as {
          cause?: { issues?: WorkflowIssue[] }
          response?: { data?: { result?: { issues?: WorkflowIssue[] } } }
        }
        const issues =
          failed?.cause?.issues ?? failed?.response?.data?.result?.issues
        if (issues && issues.length > 0) {
          setBlockingIssues(issues)
          return
        }
        setFormError(extractApiErrorMessage(err) ?? 'Action failed.')
      }
    },
    [mutation, action.key, actionLabel, onSuccess, onOpenChange],
  )

  const fields = useMemo<FormField[]>(() => {
    const generated: FormField[] = (captureFields ?? []).map(toFormField)
    if (requireReason) {
      generated.push({
        type: 'textarea',
        name: '__reason__',
        label: 'Reason',
        placeholder:
          reasonMinLength > 0
            ? `Why are you taking this action? (min ${reasonMinLength} characters)`
            : 'Why are you taking this action?',
        value: '',
        required: 'Please provide a reason.',
        validationSchema:
          reasonMinLength > 0
            ? z.string()
                .min(1, 'Please provide a reason.')
                .refine(
                  (v) => (v ?? '').trim().length >= reasonMinLength,
                  `Reason must be at least ${reasonMinLength} characters.`,
                )
            : undefined,
      })
    }
    return generated
  }, [captureFields, requireReason, reasonMinLength])

  const formName = `wf-action-${action.uuid}`
  const { Form } = useForm(
    {
      name: formName,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as FormValues
      const captured: Record<string, unknown> = {}
      for (const f of captureFields ?? []) {
        const raw = values[f.key]
        // ENTITY_SEARCH fields hold a ``{ uuid, name }`` payload; the engine
        // captures/resolves by the natural key, so decompose to the uuid
        // string (empty for a free-text non-member entry).
        if (f.type === 'entity-search' && raw && typeof raw === 'object') {
          captured[f.key] = (raw as { uuid?: string }).uuid ?? ''
        } else {
          captured[f.key] = raw
        }
      }
      // The engine treats ``preconditions`` as a reserved key inside
      // captured_values - see ``apps.workflows.engine.execute_action``.
      if (preconditions.length > 0) {
        captured.preconditions = preconditionResults
      }
      const reasonVal = (values['__reason__'] as string | undefined) || ''
      if (exceptionBinding && !exceptionGrantId) {
        // Defer to the exception modal - the single confirmation + reason
        // surface. Stash the captured values so granting the exception
        // completes the action in one step (WPS-117).
        setPendingCaptured(captured)
        setExceptionModalOpen(true)
        return
      }
      await runAction(captured, reasonVal, exceptionGrantId)
    },
    [
      captureFields,
      preconditions.length,
      preconditionResults,
      exceptionBinding,
      exceptionGrantId,
      runAction,
    ],
  )

  const exceptionReasonOptions = useMemo(() => {
    const options = exceptionReasons
      .filter((reason) => reason.is_active)
      .map((reason) => ({
        code: reason.key,
        label: reason.title,
      }))
    if (
      exceptionBinding?.suggested_exception_key &&
      !options.some((option) => option.code === exceptionBinding.suggested_exception_key)
    ) {
      options.unshift({
        code: exceptionBinding.suggested_exception_key,
        label:
          exceptionBinding.suggested_exception_title ||
          exceptionBinding.suggested_exception_key,
      })
    }
    return options
  }, [exceptionBinding, exceptionReasons])

  async function handleExceptionConfirm(payload: WorkflowIssueResolvePayload) {
    const grant = await createExceptionGrant.mutateAsync({
      action_key: action.key,
      reason_codes: payload.reason_codes ?? [],
      comment: payload.resolution_text ?? '',
      workflow_instance: instanceUuid,
      binding: exceptionBinding?.uuid ?? null,
      context: {
        action_key: action.key,
        action_label: actionLabel,
      },
    })
    setExceptionGrantId(grant.uuid)
    setExceptionModalOpen(false)
    // The exception modal is the confirmation - complete the action now using
    // the captured values stashed at submit time, recording the exception's
    // justification as the action reason so the audit trail keeps it (WPS-117).
    await runAction(pendingCaptured ?? {}, payload.resolution_text ?? '', grant.uuid)
    setPendingCaptured(null)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{actionLabel}</DialogTitle>
          {action.ui_hints?.['description'] ? (
            <DialogDescription>
              {String(action.ui_hints['description'])}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        {pendingMessage ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-100">
            {pendingMessage}
          </div>
        ) : blockingIssues.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-[var(--ink)]">
              {t('workflows.actionBlocked', 'This action is blocked.')}
            </p>
            <ul className="flex flex-col gap-1">
              {blockingIssues.map((issue) => (
                <li
                  key={issue.uuid}
                  className="rounded-md border border-amber-500/30 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-100"
                >
                  <span className="font-medium">{issue.summary}</span>
                  {issue.detail ? (
                    <span className="ml-1 opacity-80">- {issue.detail}</span>
                  ) : null}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[var(--ink-3)]">
              {t(
                'workflows.actionBlockedHint',
                'Resolve what is listed above, then try again.',
              )}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {exceptionBinding ? (
              <div className="rounded-md border border-amber-500/30 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-100">
                {t('workflows.actionRequiresException', {
                  defaultValue:
                    'This action requires an exception. Confirming opens the exception form, where you record the reason and complete the action in one step.',
                })}
              </div>
            ) : null}
            {preconditions.length > 0 ? (
              <PreconditionsPanel
                specs={preconditions}
                values={preconditionResults}
                onChange={setPreconditionResults}
              />
            ) : null}
            <Form className="flex flex-col gap-3" />
            {formError && (
              <p className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                {formError}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onOpenChange(false)
            }}
          >
            {pendingMessage ? 'Close' : 'Cancel'}
          </Button>
          {!pendingMessage && blockingIssues.length === 0 && (
            <Button
              type="submit"
              form={formName}
              disabled={mutation.isPending || !allPreconditionsMet}
              variant={
                action.ui_hints?.['variant'] === 'destructive'
                  ? 'destructive'
                  : 'default'
              }
              title={
                !allPreconditionsMet
                  ? 'Complete the items above to continue'
                  : undefined
              }
            >
              {mutation.isPending ? 'Working…' : `Confirm ${actionLabel}`}
            </Button>
          )}
        </DialogFooter>
        </DialogContent>
      </Dialog>
      {exceptionBinding ? (
        <ExceptionModal
          uuid={null}
          open={exceptionModalOpen}
          onOpenChange={setExceptionModalOpen}
          preview={{
            context: {
              subject: {
                kind: 'Workflow action',
                title: actionLabel,
                subtitle: action.key,
              },
              meta_sections: [],
              trigger: {
                title: actionLabel,
                description:
                  'This action has been configured to require an exception before it can continue.',
                breakdown: [],
              },
            },
            reasonOptions: exceptionReasonOptions,
            defaultReasonCode: exceptionBinding.suggested_exception_key || undefined,
            confirmLabel: 'Apply exception',
            onConfirm: handleExceptionConfirm,
          }}
        />
      ) : null}
    </>
  )
}
